import type { Credito } from "../types/caja.types";
import {
  runTransaction,
  txRead,
  txWrite,
} from "@/lib/persistence/transactionalStore";

/** Clave actual de cuentas de crédito. */
export const LS_CREDITOS = "caja_creditos";
/**
 * Clave legacy previa al renombre fiado→crédito. Solo se usa para migrar
 * datos ya persistidos: nunca se vuelve a escribir.
 */
const LS_CREDITOS_LEGACY = "caja_fiados";

/**
 * Lectura pura (sin escritura) con soporte de la clave legacy.
 * Segura dentro de transacciones activas: no abre transacciones anidadas.
 * Devuelve null si ninguna de las dos claves existe.
 */
export function readCreditosRawConLegacy(): Credito[] | null {
  const actual = txRead<Credito[] | null>(LS_CREDITOS, null);
  if (actual !== null) return Array.isArray(actual) ? actual : [];
  const legacy = txRead<Credito[] | null>(LS_CREDITOS_LEGACY, null);
  if (legacy === null || !Array.isArray(legacy)) return null;
  return legacy;
}

/**
 * Lee la lista de créditos; si la clave nueva no existe pero sí la legacy,
 * la copia a la clave nueva dentro de una transacción (migración idempotente).
 * No llamar DENTRO de una transacción activa (usar readCreditosRawConLegacy).
 */
function readCreditosConMigracion(): Credito[] | null {
  const list = readCreditosRawConLegacy();
  if (list === null) return null;
  if (txRead<Credito[] | null>(LS_CREDITOS, null) === null) {
    try {
      runTransaction(() => {
        if (txRead<Credito[] | null>(LS_CREDITOS, null) === null) {
          txWrite(LS_CREDITOS, list);
        }
      });
    } catch {
      /* entorno sin almacenamiento: se devuelve el dato legacy igual */
    }
  }
  return list;
}

/** Créditos en almacenamiento local (con migración de la clave legacy). */
export function readAllCreditosFromStorage(): Credito[] {
  try {
    return readCreditosConMigracion() ?? [];
  } catch {
    return [];
  }
}

/** Crédito actual desde almacenamiento local (estado vivo con abonos). */
export function readCreditoById(
  creditoId: string | null | undefined,
): Credito | null {
  if (!creditoId?.trim()) return null;
  try {
    const list = readCreditosConMigracion();
    if (!list) return null;
    return list.find((f) => f.id === creditoId) ?? null;
  } catch {
    return null;
  }
}
